/**
 * Created by ivo on 27.01.17.
 */

import _ from 'underscore';
import update from 'immutability-helper';
import CONFIG from "../config";
import Log from '../Log';
import RestSource from './RestSource';
import {RestAction, GetCollectionRestAction, GetRestAction, PostRestAction, PutRestAction, DelRestAction} from "./RestAction";
import NProgress from 'nprogress';

/**
 * Axios response object
 * @typedef {Object} AxiosResponse
 * @property {Object} data
 * @property {number} status
 * @property {string} statusText
 * @property {Object} headers
 * @property {Object} config
 */

/**
 * Axios error response object
 * @typedef {Object} AxiosErrorResponse
 * @property {string} message
 * @property {AxiosResponse} response
 * @property {Object} config
 */

/**
 * @typedef {Object} Link
 * @property {string} @href
 * @property {string} @rel
 */
/**
 * Stores list of records (record is object of field and values) and implements CRUD operations over this.
 * Works together with source based by RestSource (please see the documentation of this source about how the source should work).
 *
 * State changes:
 *
 * onActionStart() ->   state.actionStack.push(action); state.isLoading = true;
 * onActionSuccess(), onActionFailed() -> state.isLoading = false; state.action = state.actionStack.pop()
 *
 */
class BaseRestStore {
    /**
     * Constructor.
     * @param {BaseRestActions} actionClass action class used by this store
     * @param {string} resource resource used by this list store
     * @param {string} idFieldName ID field name for this resource, default is "id"
     */
    constructor(actionClass, resource, idFieldName = "id") {
        if (!idFieldName) idFieldName = "id";
        /**
         * @protected
         * @type {string}
         */
        this.resource = resource;
        /**
         * @protected
         * @type {string}
         */
        this.resourceCollection = resource + "Collection";
        /**
         * @protected
         * @type {string}
         */
        this.idFieldName = idFieldName;

        /**
         * @protected
         * @type {BaseRestStoreState}
         */
        this.state = this.getClearState();

        this.actionClass = actionClass;
        this.getActions = this.getActions.bind(this);
        this.exportPublicMethods({
            getActions: this.getActions
        });

        this.bindActions(actionClass);
        /**
         * @protected
         * @type {RestSource}
         */
        this.listSource = this.createRestSource(actionClass, resource);
        this.exportAsync(this.listSource.getExportedMethods());
    }

    getActions() {
        return this.actionClass;
    }

    /**
     * @protected
     */
    clearState() {
        this.saveState(this.getClearState());
    }

    /**
     * @protected
     * @returns {BaseRestStoreState}
     */
    getClearState() {
        /**
         * BaseRestStore state
         * @typedef {Object} BaseRestStoreState BaseRestStore state
         * @property {string} idFieldName ID field name
         * @property {boolean} isLoading If the data exchange is currently in progress
         * @property {RestAction} action Action firing state change
         * @property {RestAction[]} actionStack Currently active actions
         * @property {RestApiErrorResponse} error Last command error object in case when last command failed
         * @property {Array} data Loaded list data (contains only records for current page!)
         * @property {Link[]} links Links for data
         * @property {Object} currentRecord Loaded current record data
         * @property {number} recordCount Total number of rows currently requested
         * @property {boolean} estimated Is the record count estimated or exact number
         * @property {number} pageSize Max record count in page (0 if no pages)
         * @property {number} firstRecord First record number in list data (0-based)
         * @property {Array} sort Sort fields as an array. Prefix sort field name with "-" to get descending sort for this field. Default is ["+id"]
         * @property {Object} filter Filter - object of field names and values
         * @property {Object} editRecord Temporary storage for currently edited record
         * @property {boolean} editRecordIsChanged Is editRecord buffer changed or not
         * @property {Object} editErrors Error information of current editRecord content. Should be in form {fieldName: errorMessage} or {fieldName: [errorMessage, ...]} or null (if there are no errors)
         * @property {boolean} editError True if there are currently validation errors in edit form
         */
        return {
            idFieldName: this.idFieldName,   // ID field name
            isLoading: false,           // If the data exchange is currently in progress
            action: null,               // Last executed action
            actionStack: [],            // Action stack containing currently in progress actions
            data: [],                   // Loaded list data (contains only records for current page!)
            links: null,                // Links for data
            currentRecord: null,        // Loaded current record data
            firstRecord: 0,             // First record index (starting from 0)
            recordCount: 0,             // Total number of rows currently requested
            pageSize: CONFIG.pageSize,  // Max record count in page
            sort: [this.idFieldName],                 // Sort fields as an array. Prefix sort field name with "-" to get descending sort for this field. Default is ["id"]
            filter: null,               // Filter - object of field names and values
            editRecord: null,           // Temporary storage for data of currently edited record. Store user's responsibility is to save this data and clear out when not needed anymore.
            editRecordIsChanged: false, // Indicates if the editRecord buffer is modified or not. Automatically updated.
            editErrors: null,
            editError: false
        };
    }

    /**
     * This method is used everywhere instead of saveState().
     * Child classes can overload this method for providing additional custom
     * processing before state change.
     * @protected
     * @param newState
     */
    saveState(newState) {
        this.setState(newState);
    }

    /**
     * @protected
     * @param {BaseRestActions} actionClass
     * @param {string} resource
     * @returns {RestSource}
     */
    createRestSource(actionClass, resource) {
        return new RestSource(actionClass, resource);
    }

    /**
     * Starts async action
     * @protected
     * @param {RestAction} action Action object
     */
    onActionStart(action) {
        //if (this.getInstance().isLoading()) return;
        //stateChange = this.callActionStart(stateChange);
        if (action == null || this.isDuplicatedAction(action)) return;

        action = this.preprocessAction(action);

        if (this.state.isLoading) NProgress.inc();
        else NProgress.start();


        let p = this.getInstance().actionStart(action);
        // We need to catch the exception - this is already processed but if we do not catch this we receive warning in console
        p.catch(function () {});
        action = update(action, {$merge:{state: "started"}});

        const actionStack = update(this.state.actionStack, {$push: [action]});

        this.saveState({
            isLoading: true,
            action: action,
            actionStack: actionStack
        });
    }

    /**
     *
     * @param {RestAction} action
     */
    preprocessAction(action) {
        if (action.method === "save") {
            // Modify to correct method:
            if (this.state.currentRecord && this.state.currentRecord.id) {
                action = update(action, {$merge:{
                    method: "put",
                    id: this.state.currentRecord.id,
                    resource: this.state.editRecord
                }});
            }
            else {
                action = update(action, {$merge:{
                    method: "post",
                    resource: this.state.editRecord
                }});
            }
        }
        return action;
    }

    /**
     * Finds action from action stack based by action method name
     * @param {string} method Action method name
     * @returns {RestAction}
     */
    getActionByMethod(method) {
        return _.find(this.state.actionStack, function (a) {
            return a.method === method;
        });
    }

    /**
     * Finds action from action stack based by action ID
     * @param {string} actionId Action ID
     * @returns {RestAction}
     */
    getActionById(actionId) {
        return _.find(this.state.actionStack, function (a) {
            return a.actionId === actionId;
        });
    }

    getActionListWithoutActionById(actionId) {
        return _.reject(this.state.actionStack, function (a) {
            return a.actionId === actionId;
        });
    }

    isDuplicatedAction(action) {
        let dupAction = _.find(this.state.actionStack, function (a) {
                return a.isEqual(action);
            });
        if (dupAction) return true;
        return false;
    }

    /**
     * Triggered by RestSource when the action is successfully completed.
     * @protected
     * @param {RestApiResponse} response RestApi response object
     */
    onActionSuccess(response) {
        let action = this.getActionById(response.requestId);
        if (!action) return;
        action = this.updateActionFromResponse(action, response);
        let stateChange = this.callActionSuccess(action, response);
        this.finishActionInState(action, stateChange);
    }

    /**
     *
     * @param {RestAction} action
     * @param {RestApiResponse} response
     * @returns {RestAction} Modified action
     */
    updateActionFromResponse(action, response) {

        let newState = {};
        newState.state = "finished";

        if (response.resourceCollection) {
            if (response.resourceCollection.items) {
                newState.responseData = response.resourceCollection.items;
                newState.responseRecordCount = response.resourceCollection.items.length;
                newState.responseLinks = response.resourceCollection.link;
            }
        } else {
            newState.responseData = response.resource;
            if (response.resource && response.resource.link)
                newState.responseLinks = response.resource.link;
        }
        if (response.range) {
            if (response.range.total) newState.responseRecordCount = response.range.total;
            if (response.range.estimated) newState.responseRecordCountEstimated = response.range.estimated;

            newState.firstRecord = response.range.first;
            newState.sort = response.range.sort;
            if (_.has(response.range, "limit")) newState.pageSize = response.range.limit;
        }
        if (response.error) {
            newState.error = response.error;
            newState.isError = true;
        } else {
            newState.error = null;
            newState.isError = false;
        }
        return update(action, {$merge:newState});
    }

    finishActionInState(action, stateChange) {
        if (!stateChange) stateChange = {};
        let newActionStack = this.getActionListWithoutActionById(action.actionId);
        let newState = _.extend({}, stateChange, {
            isLoading: (newActionStack != null && newActionStack.length > 0),
            action: action,
            actionStack: newActionStack
        });
        this.saveState(newState);
        if (newState.isLoading) NProgress.inc();
        else NProgress.done();
    }

    /**
     * Triggered by RestSource when the action is completed with error.
     * @protected
     * @param {RestApiErrorResponse} response Axios error response object
     */
    onActionFailed(response) {
        let action = this.getActionById(response.requestId);
        action = this.updateActionFromResponse(action, {error: response});
        let stateChange = this.callActionFailed(action, response);
        this.finishActionInState(action, stateChange);
    }

    /**
     * @protected
     * @param settings
     */
    onGetCollection(settings) {
        this.onActionStart(new GetCollectionRestAction(this.state, settings));
    }

    /**
     * @protected
     * @param id
     */
    onGet(id) {
        this.onActionStart(new GetRestAction(id));
    }

    /**
     * @protected
     * @param row
     */
    onPost(row) {
        this.onActionStart(new PostRestAction(row));
    }

    /**
     * @protected
     * @param id
     */
    onDel(id) {
        this.onActionStart(new DelRestAction(id));
    }

    /**
     * @protected
     * @param info
     */
    onPut(info) {
        this.onActionStart(new PutRestAction(info.id, info.row));
    }

    /**
     * Set filter and reload data
     * @protected
     * @param {Object} filter
     */
    onSetFilter(filter) {
        this.onActionStart(new GetCollectionRestAction(this.state, {filter: filter}));
    }

    /**
     * Set page size and reaload data
     * @protected
     * @param {number} size
     */
    onSetPageSize(size) {
        if (size < 0) size = 0;
        this.onActionStart(new GetCollectionRestAction(this.state, {pageSize: size}));
    }

    /**
     * Go to specified page number
     * @protected
     * @param {number} recNo
     */
    onGotoRecord(recNo) {
        if (recNo < 0) recNo = 0;
        this.onActionStart(new GetCollectionRestAction(this.state, {firstRecord: recNo}));
    }

    /**
     * Set sort fields and directions
     * @protected
     * @param {array} sort Sort fields specification
     */
    onSetSort(sort) {
        if (sort == null) sort = [this.state.idFieldName];
        this.onActionStart(new GetCollectionRestAction(this.state, {sort: sort}));
    }

    /**
     * Executed when list is successfully loaded
     * @protected
     * @param {RestAction} action
     * @returns {Object} State change object
     */
    onGetCollectionSuccess(action) {
        let data = [];
        if (!action.responseData) Log.error("Response object does not contain resourceCollection property");

        return {
            data: action.responseData,
            links: action.responseLinks,
            recordCount: action.responseRecordCount,
            estimated: action.responseRecordCountEstimated,
            firstRecord: action.firstRecord,
            pageSize: action.pageSize,
            sort: action.sort,
            filter: action.filter
        };
    }

    /**
     * Executed when record is inserted successfully
     * @protected
     * @param {RestAction} action
     * @returns {Object} State change object
     */
    onPostSuccess(action) {
        let addRow = action.resource;
        if (action.responseData) addRow = action.responseData;
        let data = update(this.state.data, {$unshift: [addRow]});
        // let data = this.state.data.slice();
        // data.unshift(addRow);
        if (this.state.pageSize > 0 && data.length > this.state.pageSize) {
            data = update(data, {$splice: [[-1, 1]]});
            // data.pop();
        }

        return {
            data: data,
            currentRecord: action.responseData,
            recordCount: this.state.recordCount + 1,
        };
    }

    /**
     * Executed when record is deleted successfully
     * @protected
     * @param {RestAction} action
     * @returns {Object} State change object
     */
    onDelSuccess(action) {
        let result = {
            recordCount: this.state.recordCount - 1,
            currentRecord: null
        };

        let idx = _.findIndex(this.state.data, _.matches({[this.state.idFieldName]: action.id}));
        if (idx >= 0) {
            // data = this.state.data.slice();
            // data.splice(idx, 1);
            result.data = update(this.state.data, {$splice: [[idx, 1]]});
        }

        return result;
    }

    /**
     * Executed when record is updated successfully
     * @protected
     * @param {RestAction} action
     * @returns {Object} State change object
     */
    onPutSuccess(action) {
        let result = {
            currentRecord: action.responseData
        };

        let idx = _.findIndex(this.state.data, _.matches({[this.state.idFieldName]: action.id}));
        if (idx >= 0) {
            //data = this.state.data.slice();
            //data[idx] = action.resource;
            //if (action.responseData) data[idx] = action.responseData;
            result.data = update(this.state.data, {[idx]: {$set: action.responseData || action.resource}});
        }

        return result;
    }

    /**
     * Executed when record is readed successfully
     * @protected
     * @param {RestAction} action
     * @returns {Object} State change object
     */
    onGetSuccess(action) {
        let result = {
            currentRecord: action.responseData
        };

        let idx = _.findIndex(this.state.data, _.matches({[this.state.idFieldName]: action.id}));
        if (idx >= 0 && action.responseData) {
            //data = this.state.data.slice();
            //data[idx] = action.responseData;
            result.data = update(this.state.data, {[idx]: {$set: action.responseData}});
        }

        return result;
    }

    onClearCollection() {
        this.saveState({action: null, data: []});
    }

    onClearCurrentRecord() {
        this.saveState({action: null, currentRecord: null});
    }

    onSetCurrentRecord(record) {
        this.saveState({action: null, currentRecord: record});
    }

    onModifyCurrentRecord(currentRecord) {
        // Patch record data:
        let newState = {
            action: null
        };
        if (this.state.currentRecord) {
            if (currentRecord) newState.currentRecord = update(this.state.currentRecord, {$merge: currentRecord});
        } else {
            if (currentRecord) newState.currentRecord = _.clone(currentRecord);
        }
        this.saveState(newState);
    }

    onEditCurrentRecord() {
        // Duplicate current record data:
        this.saveState({
            action: null,
            editRecord: this.prepareRecordForEditing(this.state.currentRecord),
            editRecordIsChanged: false,
            editErrors: null,
            editError: false
        });
    }

    prepareRecordForEditing(data) {
        // Remove link element:
        data = update(data, {$unset: ["link"]});
        return data;
    }

    onSaveCurrentRecord() {
        if (this.state.currentRecord && this.state.currentRecord.id)
            this.onActionStart(new PutRestAction(this.state.currentRecord.id, this.state.editRecord));
        else
            this.onActionStart(new PostRestAction(this.state.editRecord));
    }

    onModifyEditRecord(info) {
        // Patch record data:
        // let newRecord = _.extend({}, this.state.editRecord, info.editRecord);
        let newState = {
            action: null,
            editRecordIsChanged: (info.editRecordIsChanged ? true : this.state.editRecordIsChanged)
        };
        if (this.state.editRecord) {
            if (info.editRecord) newState.editRecord = update(this.state.editRecord, {$merge: info.editRecord});
        } else {
            if (info.editRecord) newState.editRecord = _.clone(info.editRecord);
        }
        if (info.editErrors) newState.editErrors = _.clone(info.editErrors);
        this.saveState(newState);
    }

    onSetEditRecord(info) {
        // Patch record data:
        // let newRecord = _.extend({}, this.state.editRecord, info.editRecord);
        let newState = {
            action: null,
            editRecord: info.editRecord,
            editRecordIsChanged: (info.editRecordIsChanged ? true : this.state.editRecordIsChanged),
            editErrors: null,
            editError: false
        };
        this.saveState(newState);
    }

    onSetEditErrors(editErrors) {
        this.saveState({action: null, editErrors: _.clone(editErrors)});
    }

    onSetErrorFlag() {
        this.saveState({action: null, editError: true});
    }

    onClearErrorFlag() {
        this.saveState({action: null, editError: false});
    }

    onClearEditRecord() {
        this.saveState({action: null, editRecord: null, editRecordIsChanged: false, editErrors: null, editError: false});
    }

    onClearEditRecordChangedFlag() {
        this.saveState({action: null, editRecordIsChanged: false});
    }

    /* Internally used methods */

    /**
     * Returns action name
     * @private
     * @param {RestAction} action
     * @returns {*}
     */
    getActionName(action) {
        return action.method;
    }

    /**
     * Returns action callback method name
     * @private
     * @param action
     * @param suffix
     * @returns {string}
     */
    getActionMethod(action, suffix) {
        let actionName = this.getActionName(action);
        return "on" + actionName.substring(0, 1).toUpperCase() + actionName.substring(1) + suffix;
    }

    /**
     * Calls action start callback method
     * @private
     * @param stateChange
     * @returns {*}
     */
    callActionStart(stateChange) {
        let method = this.getActionMethod(stateChange.action, "");
        if (this[method]) return this[method](stateChange);
        return stateChange;
    }

    /**
     * Calls action successful completion callback method
     * @private
     * @param {RestAction} action
     * @param {RestApiResponse} response
     * @returns {*}
     */
    callActionSuccess(action, response) {
        let method = this.getActionMethod(action, "Success");
        if (this[method]) return this[method](action);
        return {};
    }

    /**
     * Calls action failed completion callback method
     * @private
     * @param {RestAction} action
     * @param {RestApiErrorResponse} response
     * @returns {*}
     */
    callActionFailed(action, response) {
        let method = this.getActionMethod(action, "Failed");
        if (this[method]) return this[method](action);
        return {};
    }

    /**
     * Returns specified list row.
     * @protected
     * @param id
     * @returns {*}
     */
    getItem(id) {
        for (let item of this.state.data) {
            if (item[this.state.idFieldName] === id) return item;
        }
        return null;
    }

}

export default BaseRestStore;
